import { useParams } from "react-router-dom";

import Box from "@mui/material/Box";

import { UserAnalyticsService } from "@analytics/index";

import AddToPortfolio from "@components/Forms/AddToPortfolio";
import ResourceLink from "@components/ResourceLink";

import C from "@constants/constants";

import useRemoveCompanies from "@hooks/useRemoveCompanies";

import { CompanyType } from "@interfaces/company.type";
import { IFetchCompanySearch } from "@interfaces/ipsearch.type";

import SmartSearchForm from "@pages/Companies/components/SmartSearchForm";

import { useAddCompaniesToTechnologyQuery, useCreateTechnologyFromCompanyIdsQuery } from "@queries/portfolio.query";

import { PopupActions } from "@store/popupStore";

interface IUseDirectoryToolbar {
  allCompanies: CompanyType[];
  fetchCompanySearch: ({ preferredIndustries, queryString }: IFetchCompanySearch) => void;
  isFilterOpen: boolean;
  searchId: string;
  selectedIds: string[];
  setIsFilterOpen: (isOpen: boolean) => void;
  technologyName?: string;
  workflow: string;
}

const useDirectoryToolbar = ({
  allCompanies,
  fetchCompanySearch,
  isFilterOpen,
  searchId,
  selectedIds,
  setIsFilterOpen,
  technologyName = "",
  workflow,
}: IUseDirectoryToolbar) => {
  const parameters = useParams();

  const { mutateAsync: createTechnologyFromId } = useCreateTechnologyFromCompanyIdsQuery();
  const { mutateAsync: addCompaniesToTechnology } = useAddCompaniesToTechnologyQuery();

  const technologyId = parameters.technologyId;

  const toggleFilterVisibility = () => {
    setIsFilterOpen(!isFilterOpen);
  };

  const companiesToAddOrRemove = allCompanies
    .filter((company) => selectedIds.includes(company.id))
    .map((company) => company.id)
    .map((id) => id);

  const removeCompanies = useRemoveCompanies();

  const handleRemoveCompanies = () => {
    if (!technologyId) return;
    void removeCompanies({
      companyIds: companiesToAddOrRemove,
      name: technologyName,
      technologyId,
    });
  };

  const handleAddCompaniesToTechnology = () => {
    PopupActions.openModal({
      content: (
        <AddToPortfolio
          addToTechnology={async (technologyId, technologyName) => {
            await addCompaniesToTechnology({
              companyIds: companiesToAddOrRemove,
              searchId,
              technologyId,
              technologyName,
            });
            PopupActions.closeModal();
          }}
          createTechnology={async (name, description) => {
            await createTechnologyFromId({
              companyIds: companiesToAddOrRemove,
              description,
              name,
              searchId,
            });
            PopupActions.closeModal();
          }}
        />
      ),
      title: "Save to Technology",
    });
  };

  const handleSmartSearch = () => {
    PopupActions.openModal({
      content: <SmartSearchForm fetchCompanySearch={fetchCompanySearch} />,
      title: (
        <Box sx={{ alignItems: "center", display: "flex", gap: 1 }}>
          Portfolio Search
          <ResourceLink resourceLink={C.LOOM_IP_SEARCH} workflow="smart-search" />
        </Box>
      ),
    });
  };
  //

  return {
    handleAddCompaniesToTechnology,
    handleRemoveCompanies,
    handleSmartSearch,
    toggleFilterVisibility,
  };
};

export default useDirectoryToolbar;
